import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AdminUser from '../../models/admin/AdminUser';
import BackendService from '../../services/admin/BackendService';
import FidesChatAdminService, { ChatParticipant } from '../../services/admin/FidesChatAdminService';
import {
  formatAdminSearchLabel,
  panelAdminExternalId,
} from '../../components/admin/fidesChatPanelUtils';
import { FIDES_CHAT_REFRESH_EVENT } from './useFidesChatInbox';

interface UseFidesChatPanelTeamOptions {
  openConversationId: string | null;
  isPanelOpen: boolean;
  isTranscriptMode: boolean;
  selectedOwnerAdminExternalId: string | null | undefined;
}

export function useFidesChatPanelTeam({
  openConversationId,
  isPanelOpen,
  isTranscriptMode,
  selectedOwnerAdminExternalId,
}: UseFidesChatPanelTeamOptions) {
  const [isClaiming, setIsClaiming] = useState(false);
  const [participants, setParticipants] = useState<ChatParticipant[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [currentAdminExternalId, setCurrentAdminExternalId] = useState<string | null>(null);
  const [adminSearchOptions, setAdminSearchOptions] = useState<AdminUser[]>([]);
  const [adminSearchLoading, setAdminSearchLoading] = useState(false);
  const [inviteSearchInput, setInviteSearchInput] = useState('');
  const [transferSearchInput, setTransferSearchInput] = useState('');
  const [transferOwnerValue, setTransferOwnerValue] = useState<AdminUser | null>(null);
  const [isAddingParticipant, setIsAddingParticipant] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [teamSectionExpanded, setTeamSectionExpanded] = useState(false);
  const [ownershipHintExternalId, setOwnershipHintExternalId] = useState<string | null>(null);

  const refreshParticipants = useCallback(async (conversationId: string) => {
    setParticipantsLoading(true);
    try {
      const data = await FidesChatAdminService.listParticipants(conversationId);
      setParticipants(data.participants);
    } catch (participantsError) {
      console.error('Failed to load chat participants', participantsError);
      setParticipants([]);
    } finally {
      setParticipantsLoading(false);
    }
  }, []);

  const debouncedAdminSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (query.trim().length < 2) {
          setAdminSearchOptions([]);
          setAdminSearchLoading(false);
          return;
        }
        setAdminSearchLoading(true);
        new BackendService()
          .searchAdminUser(query)
          .then((results) => setAdminSearchOptions(results))
          .catch(() => setAdminSearchOptions([]))
          .finally(() => setAdminSearchLoading(false));
      }, 300),
    [],
  );

  useEffect(() => () => debouncedAdminSearch.cancel(), [debouncedAdminSearch]);

  useEffect(() => {
    void new BackendService()
      .getCurrentUser()
      .then((admin) => setCurrentAdminExternalId(panelAdminExternalId(admin)))
      .catch(() => setCurrentAdminExternalId(null));
  }, []);

  useEffect(() => {
    if (!openConversationId || !isPanelOpen || isTranscriptMode) {
      setParticipants([]);
      return;
    }
    void refreshParticipants(openConversationId);
  }, [isPanelOpen, openConversationId, isTranscriptMode, refreshParticipants]);

  useEffect(() => {
    setOwnershipHintExternalId(null);
    setInviteSearchInput('');
    setTransferSearchInput('');
    setAdminSearchOptions([]);
    setTeamSectionExpanded(false);
  }, [openConversationId]);

  const participantOwner = useMemo(
    () => participants.find((participant) => participant.isOwner) ?? null,
    [participants],
  );
  const effectiveOwnerExternalId =
    selectedOwnerAdminExternalId ??
    participantOwner?.adminExternalId ??
    ownershipHintExternalId ??
    null;
  const effectiveOwnerName = participantOwner?.adminName?.trim() || null;
  const isOwner = Boolean(
    currentAdminExternalId &&
    (effectiveOwnerExternalId === currentAdminExternalId ||
      participants.some(
        (participant) =>
          participant.isOwner && participant.adminExternalId === currentAdminExternalId,
      ) ||
      ownershipHintExternalId === currentAdminExternalId),
  );
  const isUnowned = !effectiveOwnerExternalId;
  const isParticipant = Boolean(
    currentAdminExternalId &&
    participants.some((participant) => participant.adminExternalId === currentAdminExternalId),
  );
  const canInviteAdmins = !isUnowned && (isParticipant || isOwner);

  const participantExternalIds = useMemo(
    () => new Set(participants.map((participant) => participant.adminExternalId)),
    [participants],
  );

  const inviteSearchOptions = useMemo(
    () =>
      adminSearchOptions.filter((admin) => {
        const id = panelAdminExternalId(admin);
        return id && !participantExternalIds.has(id) && id !== currentAdminExternalId;
      }),
    [adminSearchOptions, participantExternalIds, currentAdminExternalId],
  );

  const handleInviteSearchInputChange = useCallback(
    (value: string) => {
      setInviteSearchInput(value);
      debouncedAdminSearch(value);
    },
    [debouncedAdminSearch],
  );

  const handleTransferSearchInputChange = useCallback(
    (value: string) => {
      setTransferSearchInput(value);
      debouncedAdminSearch(value);
    },
    [debouncedAdminSearch],
  );

  const handleClaim = useCallback(
    async (onSuccess: (message: string) => void, onError: (message: string) => void) => {
      if (!openConversationId || isClaiming) return;
      setIsClaiming(true);
      try {
        await FidesChatAdminService.claimConversation(openConversationId);
        if (currentAdminExternalId) {
          setOwnershipHintExternalId(currentAdminExternalId);
        }
        window.dispatchEvent(new Event(FIDES_CHAT_REFRESH_EVENT));
        await refreshParticipants(openConversationId);
        onSuccess('You are now the owner of this chat.');
      } catch (claimError) {
        console.error('Failed to claim conversation', claimError);
        onError('Unable to claim conversation.');
      } finally {
        setIsClaiming(false);
      }
    },
    [openConversationId, isClaiming, currentAdminExternalId, refreshParticipants],
  );

  const handleInviteAdmin = useCallback(
    async (
      admin: AdminUser | null,
      onSuccess: (message: string) => void,
      onError: (message: string) => void,
    ) => {
      const invitedAdminId = admin ? panelAdminExternalId(admin) : '';
      if (!openConversationId || !invitedAdminId || isAddingParticipant) return;
      setIsAddingParticipant(true);
      try {
        await FidesChatAdminService.addParticipant(openConversationId, invitedAdminId);
        setInviteSearchInput('');
        setAdminSearchOptions([]);
        window.dispatchEvent(new Event(FIDES_CHAT_REFRESH_EVENT));
        await refreshParticipants(openConversationId);
        onSuccess(`${admin ? formatAdminSearchLabel(admin) : 'Admin'} added to the chat.`);
      } catch (addError) {
        console.error('Failed to add participant', addError);
        onError('Unable to invite admin.');
      } finally {
        setIsAddingParticipant(false);
      }
    },
    [openConversationId, isAddingParticipant, refreshParticipants],
  );

  const handleRemoveParticipant = useCallback(
    async (
      adminExternalId: string,
      onSuccess: (message: string) => void,
      onError: (message: string) => void,
    ) => {
      if (!openConversationId) return;
      try {
        await FidesChatAdminService.removeParticipant(openConversationId, adminExternalId);
        window.dispatchEvent(new Event(FIDES_CHAT_REFRESH_EVENT));
        await refreshParticipants(openConversationId);
        onSuccess('Participant removed.');
      } catch (removeError) {
        console.error('Failed to remove participant', removeError);
        onError('Unable to remove participant.');
      }
    },
    [openConversationId, refreshParticipants],
  );

  const handleTransferOwnership = useCallback(
    async (onSuccess: (message: string) => void, onError: (message: string) => void) => {
      const newOwnerId = transferOwnerValue ? panelAdminExternalId(transferOwnerValue) : '';
      if (!openConversationId || !newOwnerId || isTransferring) return;
      setIsTransferring(true);
      try {
        await FidesChatAdminService.transferOwnership(openConversationId, newOwnerId);
        setTransferOwnerValue(null);
        window.dispatchEvent(new Event(FIDES_CHAT_REFRESH_EVENT));
        await refreshParticipants(openConversationId);
        onSuccess('Ownership transferred.');
      } catch (transferError) {
        console.error('Failed to transfer ownership', transferError);
        onError('Unable to transfer ownership.');
      } finally {
        setIsTransferring(false);
      }
    },
    [openConversationId, transferOwnerValue, isTransferring, refreshParticipants],
  );

  const wasUnownedAtSend = useCallback(() => {
    return (
      !selectedOwnerAdminExternalId &&
      !participants.some((participant) => participant.isOwner) &&
      !ownershipHintExternalId
    );
  }, [selectedOwnerAdminExternalId, participants, ownershipHintExternalId]);

  const applyOwnershipAfterSend = useCallback(() => {
    if (currentAdminExternalId) {
      setOwnershipHintExternalId(currentAdminExternalId);
    }
  }, [currentAdminExternalId]);

  const refreshAfterSend = useCallback(async () => {
    if (openConversationId) {
      window.dispatchEvent(new Event(FIDES_CHAT_REFRESH_EVENT));
      await refreshParticipants(openConversationId);
    }
  }, [openConversationId, refreshParticipants]);

  return {
    isClaiming,
    participants,
    participantsLoading,
    currentAdminExternalId,
    adminSearchOptions,
    adminSearchLoading,
    inviteSearchInput,
    transferSearchInput,
    transferOwnerValue,
    setTransferOwnerValue,
    isAddingParticipant,
    isTransferring,
    teamSectionExpanded,
    setTeamSectionExpanded,
    ownershipHintExternalId,
    effectiveOwnerExternalId,
    effectiveOwnerName,
    isOwner,
    isUnowned,
    canInviteAdmins,
    inviteSearchOptions,
    handleClaim,
    handleInviteAdmin,
    handleRemoveParticipant,
    handleTransferOwnership,
    handleInviteSearchInputChange,
    handleTransferSearchInputChange,
    wasUnownedAtSend,
    applyOwnershipAfterSend,
    refreshAfterSend,
  };
}

export default useFidesChatPanelTeam;
